<template>
  <div class="d-flex justify-content-center">
    <div class="col-12 col-md-10 col-lg-10">
      <div class="content-container container">
        <div class="mb-3">
          <pill-dropdown
            class="Timeline-filter"
            label="Timeline"
            :options="timelineOptions"
            :selected="timeline.id"
            @change="onTimelineChange"
          />
          <activity-type-filter
            class="activity-filter ml-2"
            :selected="activity"
            @change="onEventTypeChange"
          />
        </div>
        <div class="loader" v-if="isFetching">
          <activity-skeleton :hide-team="true" />
        </div>
        <div
          class="text-secondary no-result pl-2 ml-1 text-medium"
          v-else-if="activities.length === 0"
        >
          We did not find any activity, try changing the selected filter
        </div>
        <div class="activities" v-else>
          <activity
            v-for="(activity, index) in activities"
            :activity="activity"
            :key="index"
            :org-id="orgId"
            :timeline="timeline"
            :hide-top-bar="true"
            @reacted="fetchActivity(true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import ActivityTypeFilter from '@/components/activity/ActivityTypeFilter'
import PillDropdown from '@/components/board/PillDropdown'
import {
  ACTIVITY_TIMELINES,
  EVENT_TYPE_FILTER_ID,
  ACTIVITY_TYPE_ANALYTICS_NAME,
  EVENT_TYPE_FILTER_OPTIONS,
  TEAM_ORG_ACTIVITY_DEFAULT_TIMELINE
} from '@/constants/activity'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import service from '@/service'
import urlManager from '@/urlManager'
import { convertToDateString } from '@/utils/formatDate'
import showAPIError from '@/utils/showAPIError'
import ActivitySkeleton from '@/components/activity/ActivitySkeleton'
import Activity from '@/components/activity/Activity'

/**
 * This shows the activity feed for the team/org
 */
export default {
  name: 'OrgTeamActivity',
  props: ['teamId', 'orgId', 'query'],
  data() {
    return {
      isFetching: false,
      activities: [],
      timelineOptions: ACTIVITY_TIMELINES.map(({ label, id }) => ({ label, id }))
    }
  },
  components: {
    ActivitySkeleton,
    Activity,
    ActivityTypeFilter,
    PillDropdown
  },
  computed: {
    timeline() {
      const timeline = this.query.from || TEAM_ORG_ACTIVITY_DEFAULT_TIMELINE
      return ACTIVITY_TIMELINES.find(t => t.id === timeline)
    },
    activity() {
      return this.query.activity || EVENT_TYPE_FILTER_ID.checkin
    }
  },
  methods: {
    async fetchActivity(preventLoading) {
      if (!preventLoading) {
        this.isFetching = true
      }
      const { teamId, timeline } = this
      let startDate = null
      if (timeline) {
        startDate = convertToDateString(timeline.startDate)
      }
      const filterDetails = this.getSelectActivityFilterDetails()
      const activityTypeFilter = filterDetails.filterValue
        ? { [filterDetails.filterName]: filterDetails.filterValue }
        : {}
      try {
        const response = await service.client.get(urlManager.activity(this.orgId), {
          params: {
            team_id: teamId,
            start_date: startDate,
            ...activityTypeFilter
          }
        })
        this.activities = response.data
      } catch (e) {
        showAPIError(e)
      }
      this.isFetching = false
    },
    onTimelineChange(timeline) {
      this.updateRouteQuery({ from: timeline.id })
    },
    addToAnalytics() {
      const category = EVENT_CATEGORY.NAVIGATION
      let action = EVENT_ACTION.OPEN_TEAM_ACTIVITY_VIEW
      const team = this.$store.getters.teamById(this.teamId)
      if (team.isOrgLike) {
        action = EVENT_ACTION.OPEN_ORG_ACTIVITY_VIEW
      }
      trackEvent(category, action)
    },
    updateRouteQuery(queriesToUpdate) {
      const { name, params, query } = this.$route
      this.$router.push({
        name,
        params,
        query: {
          ...query,
          ...queriesToUpdate
        }
      })
    },
    getSelectActivityFilterDetails() {
      const activity = EVENT_TYPE_FILTER_OPTIONS.find(opt => opt.id === this.activity)
      if (activity) {
        return {
          filterName: activity.filterName,
          filterValue: activity.filterValue
        }
      }
      return {}
    },
    onEventTypeChange(eventType) {
      const eventId = eventType.id
      trackEvent(
        EVENT_CATEGORY.PREFERENCE,
        EVENT_ACTION.ACTIVITY_FILTER_REPORT,
        ACTIVITY_TYPE_ANALYTICS_NAME[eventId]
      )
      this.updateRouteQuery({ activity: eventId })
    }
  },
  mounted() {
    this.fetchActivity()
    this.addToAnalytics()
  },
  watch: {
    query: 'fetchActivity'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.container {
  padding-left: 50px;
}

.activities {
  margin-left: -16px;
}

.loader {
  margin-left: -16px;
  padding-top: 3px;
}

.content-container {
  margin-top: 22px;
}

@include media-breakpoint-down(md) {
  .container {
    padding-left: 0;
  }
}
</style>
